import { useState, type FormEvent } from 'react';
import { ApiError, signIn, type Session } from '../api';

/**
 * Stands in front of the whole planner. Nothing else renders until it passes —
 * which matters beyond keeping strangers out, because the map would otherwise
 * start pulling Mapbox tiles for anyone who found the URL.
 */
export function SignInScreen({
  configured,
  onSignedIn,
}: {
  configured: boolean;
  onSignedIn: (session: Session) => void;
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onSignedIn(await signIn(username, password));
    } catch (problem: unknown) {
      setError(problem instanceof ApiError ? problem.message : 'Could not sign in');
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="signin">
      <form className="signin-card" onSubmit={submit}>
        <h1>CAD</h1>
        <p className="signin-tagline">Route planner</p>

        {!configured && (
          <p className="signin-error">
            No credentials are set on this server yet. Run <code>node tools/set-web-auth.mjs</code>.
          </p>
        )}

        <label>
          Username
          <input
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            autoFocus
            required
          />
        </label>

        <label>
          Password
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
          />
        </label>

        {error && <p className="signin-error">{error}</p>}

        <button type="submit" className="primary" disabled={busy || !configured}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="fine-print">
          Shared routes still open without this. The sign-in is only for making and editing them.
        </p>
      </form>
    </div>
  );
}
